import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from './translations';

const I18nContext = createContext(null);

const STORAGE_KEY = 'app_lang';
const DEFAULT_LANG = 'ar';

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    // Get from localStorage or use default
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
    }
    return DEFAULT_LANG;
  });

  // Update document direction and lang attribute
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.body.style.direction = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  // Set language and persist to localStorage
  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);
  }, []);

  // Toggle between AR and EN
  const toggleLang = useCallback(() => {
    const newLang = lang === 'ar' ? 'en' : 'ar';
    setLang(newLang);
  }, [lang, setLang]);

  // Translation function with interpolation support
  const t = useCallback((key, params = {}) => {
    const langTranslations = translations[lang] || translations[DEFAULT_LANG];
    let text = langTranslations[key] || translations[DEFAULT_LANG][key] || key;
    
    // Replace {param} placeholders
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
    });
    
    return text;
  }, [lang]);

  // Check if current language is RTL
  const isRTL = lang === 'ar';

  const value = {
    lang,
    setLang,
    toggleLang,
    t,
    isRTL,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

// Helper to format numbers based on locale
export function formatNumber(num, lang = 'ar') {
  const locale = lang === 'ar' ? 'ar-SA' : 'en-US';
  return new Intl.NumberFormat(locale).format(num);
}

// Helper to format dates based on locale
export function formatDate(date, lang = 'ar') {
  const locale = lang === 'ar' ? 'ar-SA' : 'en-US';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date(date));
}
